import { isAnyHazmatFieldFilled } from '../../../../ltl/hazmat-validation';

import type { FreightFulfillmentPlanCommodityPreset, FreightWeightUnit } from '../../../../types/freight-responses';
import type { FreightClass, LtlCommodity } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';

// FreightWeightUnit includes 'Unknown', which is not a valid LtlCommodity weight
// unit — collapse it to undefined so the unit defaulting elsewhere applies.
const toLtlWeightUnit = (weightUnit: FreightWeightUnit): WeightUnit | undefined => (weightUnit !== 'Unknown' ? weightUnit : undefined);

/**
 * Merges a seller-saved commodity preset into the wizard's commodity form state.
 * The preset wins on every field it carries — freightClass, description, nmfcCode,
 * quantity, and weight when the preset has a non-null weight. The only field that
 * does not unconditionally overwrite is weight: a preset saved without a weight
 * (null) leaves the existing weight untouched rather than writing 0.
 *
 * `isHazmat` is derived from the resulting hazmatInfo rather than copied — the
 * payload mapper only serializes `hazmatInfo` when `isHazmat` is true, so copying a
 * stale `isHazmat` alongside a newly-applied `hazmatInfo` could silently drop hazmat
 * data from the outgoing booking payload.
 *
 * `pieceType` maps from `presetCommodity.packageType` — the nested commodity field
 * where the backend now stores piece type.
 */
export const applyCommodityPresetToCommodity = (
  commodity: LtlCommodity,
  commodityPreset: FreightFulfillmentPlanCommodityPreset,
  { isHazmatEnabled }: { isHazmatEnabled: boolean },
): LtlCommodity => {
  const { commodity: presetCommodity } = commodityPreset;

  const shouldApplyHazmat = isHazmatEnabled && presetCommodity.hazmatInfo != null;
  const hazmatInfo = shouldApplyHazmat ? presetCommodity.hazmatInfo : commodity.hazmatInfo;

  // Treat both null and 0 as "no weight" — null is the canonical absent signal going
  // forward, but presets saved before this fix stored a blank weight as 0.
  const presetHasWeight = !!presetCommodity.weight;

  return {
    ...commodity,
    productName: commodityPreset.name,
    description: presetCommodity.description ?? commodity.description,
    freightClass: (presetCommodity.freightClass ?? commodity.freightClass) as FreightClass | '',
    nmfcCode: presetCommodity.nmfcCode ?? commodity.nmfcCode,
    weight: presetHasWeight ? presetCommodity.weight! : commodity.weight,
    weightUnit: presetHasWeight ? toLtlWeightUnit(presetCommodity.weightUnit) : commodity.weightUnit,
    quantity: presetCommodity.quantity,
    numberOfPieces: presetCommodity.quantity,
    pieceType: presetCommodity.packageType ?? commodity.pieceType,
    hazmatInfo,
    isHazmat: isAnyHazmatFieldFilled(hazmatInfo),
  };
};
